import { useEffect, useRef, useState } from "react";
import { Heart, MapPin } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useFavorites } from "@/hooks/useFavorites";
import { useLanguage } from "@/contexts/LanguageContext";
import { toast } from "@/hooks/use-toast";
import { MOCK_LISTINGS } from "@/data/listings.data";
import type { Listing } from "@/types/listing.types";

const INTRO_FINISH_MS = 2400;

const COUNTRY_LABELS: Record<string, string> = {
  CA: "🇨🇦 Canada",
  US: "🇺🇸 USA",
  GB: "🇬🇧 UK",
  AU: "🇦🇺 Australia",
  FR: "🇫🇷 France",
};

function getSlug(detailUrl?: string) {
  if (!detailUrl) return null;
  const parts = detailUrl.split("/").filter(Boolean);
  return parts[parts.length - 1] ?? null;
}

function FeaturedListingCard({ listing, duplicate = false }: { listing: Listing; duplicate?: boolean }) {
  const { language } = useLanguage();
  const { isFavorite, toggleFavorite } = useFavorites();
  const navigate = useNavigate();
  const slug = getSlug(listing.detailUrl);
  const isLiked = slug ? isFavorite(slug) : false;
  const price = new Intl.NumberFormat(language === "fr" ? "fr-CA" : "en-US", {
    style: "currency",
    currency: listing.financials.currency,
    maximumFractionDigits: 0,
  }).format(listing.financials.price);

  return (
    <article
      aria-hidden={duplicate || undefined}
      className="flex h-[430px] w-[82vw] max-w-[380px] shrink-0 cursor-pointer flex-col overflow-hidden rounded-lg border border-border bg-card shadow-lg transition-shadow duration-300 hover:shadow-2xl sm:w-[360px]"
      onClick={() => listing.detailUrl && navigate(listing.detailUrl)}
    >
      <div className="relative h-48 shrink-0 overflow-hidden bg-muted">
        <img src={listing.imageUrl} alt={listing.title} className="h-full w-full object-cover" />
        <div className="absolute right-3 top-3 rounded-full bg-accent px-3 py-1 text-xs font-semibold text-accent-foreground">
          {COUNTRY_LABELS[listing.location.country] ?? listing.location.country}
        </div>
        <Button
          type="button"
          variant="secondary"
          size="icon"
          tabIndex={duplicate ? -1 : undefined}
          className={`absolute bottom-3 right-3 rounded-full shadow-lg ${isLiked ? "bg-accent text-accent-foreground hover:bg-accent/90" : "bg-card text-muted-foreground hover:bg-muted"}`}
          aria-label={language === "fr" ? "Ajouter aux favoris" : "Add to favorites"}
          onClick={(event) => {
            event.stopPropagation();
            if (!slug) return;
            toggleFavorite(slug);
            toast({
              description: isLiked
                ? language === "fr" ? "Retiré des favoris" : "Removed from favorites"
                : language === "fr" ? "Ajouté aux favoris" : "Added to favorites",
            });
          }}
        >
          <Heart className={`h-5 w-5 ${isLiked ? "fill-current" : ""}`} />
        </Button>
      </div>

      <div className="flex flex-1 flex-col p-5">
        <div className="mb-3 flex items-start justify-between gap-3">
          <h3 className="line-clamp-2 text-lg font-semibold leading-tight text-card-foreground">{listing.title}</h3>
          <span className="shrink-0 text-lg font-bold text-card-foreground">{price}</span>
        </div>
        <div className="mb-3 flex items-center gap-1.5 text-sm text-muted-foreground">
          <MapPin className="h-4 w-4 shrink-0" />
          <span className="truncate">{listing.location.city}, {listing.location.state}</span>
        </div>
        <p className="mb-4 line-clamp-3 text-sm text-muted-foreground">{listing.description}</p>
        {/* AJOUTÉ — badge pré-financement, cohérent avec /listings. Retiré à
            nouveau par Lovable lors de sa reconstruction pour le fix mobile
            (elle n'a pas connaissance de ce correctif, fait uniquement dans
            VSCode) — réintégré ici. */}
        {listing.preFinancingOffers > 0 && (
          <div className="mb-3">
            <span className="inline-flex items-center gap-1.5 bg-gradient-to-r from-[#b8924f] to-[#d4af6a] text-white px-4 py-2 rounded-full text-sm font-semibold">
              ✓ {listing.preFinancingOffers}{" "}
              {language === "fr" ? "offres de pré-financement" : "pre-financing offers"}
            </span>
          </div>
        )}
        <div className="mt-auto flex items-center justify-between gap-3 border-t border-border pt-3">
          <div className="flex min-w-0 gap-2 overflow-hidden">
            {listing.badges.slice(0, 2).map((badge, index) => (
              <span
                key={`${badge.type}-${index}`}
                className={`truncate rounded-full px-2 py-1 text-xs font-medium ${
                  badge.type === "seller_financing"
                    ? "bg-emerald-100 text-emerald-700"
                    : badge.type === "e2_visa"
                      ? "bg-blue-100 text-blue-700"
                      : "bg-amber-100 text-amber-700"
                }`}
              >
                {badge.label}
              </span>
            ))}
          </div>
          <span className="shrink-0 text-sm font-medium text-accent">{language === "fr" ? "Voir →" : "View →"}</span>
        </div>
      </div>
    </article>
  );
}

export function FeaturedListingsCarousel() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [hasEntered, setHasEntered] = useState(false);
  const [marqueeStarted, setMarqueeStarted] = useState(false);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const mobileViewport = window.matchMedia("(max-width: 767px)").matches;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      setHasEntered(true);
      observer.disconnect();
    }, {
      threshold: mobileViewport ? 0.08 : 0.3,
      rootMargin: mobileViewport ? "0px 0px -2% 0px" : "0px 0px -8% 0px",
    });
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!hasEntered) return;
    const timer = window.setTimeout(() => setMarqueeStarted(true), INTRO_FINISH_MS);
    return () => window.clearTimeout(timer);
  }, [hasEntered]);

  return (
    <div ref={sectionRef} className="relative h-[430px] overflow-hidden">
      <div className={`mx-auto flex w-max gap-4 px-4 transition-opacity duration-300 sm:gap-6 sm:px-6 md:grid md:w-auto md:max-w-7xl md:grid-cols-3 md:justify-items-center md:gap-8 lg:px-8 ${marqueeStarted ? "pointer-events-none opacity-0" : "opacity-100"}`}>
        {MOCK_LISTINGS.slice(0, 3).map((listing, index) => (
          <div
            key={listing.id}
            className={`w-[82vw] max-w-[380px] shrink-0 sm:w-[360px] md:w-full motion-reduce:animate-none motion-reduce:opacity-100 ${hasEntered ? "animate-listing-race-in" : "opacity-0"}`}
            style={{ animationDelay: `${index * 500}ms` }}
          >
            <FeaturedListingCard listing={listing} />
          </div>
        ))}
      </div>

      <div className={`group absolute inset-0 transition-opacity duration-500 ${marqueeStarted ? "opacity-100" : "pointer-events-none opacity-0"}`}>
        <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-12 bg-gradient-to-r from-background to-transparent sm:w-24" />
        <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-12 bg-gradient-to-l from-background to-transparent sm:w-24" />
        <div className="flex w-max gap-4 animate-listings-marquee motion-reduce:animate-none sm:gap-8 md:group-hover:[animation-play-state:paused]">
          {[...MOCK_LISTINGS, ...MOCK_LISTINGS].map((listing, index) => (
            <FeaturedListingCard key={`${listing.listingNumber}-${index}`} listing={listing} duplicate={index >= MOCK_LISTINGS.length} />
          ))}
        </div>
      </div>
    </div>
  );
}